// Core data model for the WHACKHALLA simulation. The sim is pure data + logic
// with no rendering dependency, so it can be unit-tested headless.

export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'

export const RARITY_ORDER: Rarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary']

export const RARITY_COLOR: Record<Rarity, number> = {
  common: 0xb8c0cc,
  uncommon: 0x4caf50,
  rare: 0x2f8fff,
  epic: 0xb44cff,
  legendary: 0xffb13d,
}

export const RARITY_WEIGHT: Record<Rarity, number> = {
  common: 100,
  uncommon: 55,
  rare: 26,
  epic: 9,
  legendary: 2,
}

/**
 * Full derived stat block for the player. Multipliers default to 1, additive
 * bonuses to 0. Recomputed from base + character + items + meta each time the
 * loadout changes.
 */
export interface Stats {
  maxHp: number
  hpRegen: number // hp per second
  moveSpeed: number // world units / second
  damage: number // global damage multiplier
  attackSpeed: number // global cooldown multiplier (higher = faster)
  critChance: number // 0..1
  critMult: number // damage multiplier on crit
  area: number // AoE / projectile-size multiplier
  projectileSpeed: number // multiplier
  projectileCount: number // additive bonus projectiles
  duration: number // effect/projectile lifetime multiplier
  pickupRange: number // world units
  xpGain: number // multiplier
  goldGain: number // multiplier
  armor: number // flat damage reduction (diminishing)
  dodge: number // 0..1 chance to ignore a hit
  luck: number // shifts rarity / proc rolls
  lifesteal: number // fraction of damage dealt returned as hp
  cooldownReduction: number // additive 0..1, stacks into attackSpeed
  revives: number
  knockback: number // multiplier
  thorns: number // reflect fraction
  statusDamage: number // bonus damage multiplier vs enemies suffering a status (burn/poison/freeze/slow) — DoT/CC synergy
  maxShield: number // flat regenerating shield capacity (absorbs damage before HP; refills slowly out of the line of fire)
}

export function baseStats(): Stats {
  return {
    maxHp: 100,
    hpRegen: 0.5,
    moveSpeed: 5.2,
    damage: 1,
    attackSpeed: 1,
    critChance: 0.05,
    critMult: 2,
    area: 1,
    projectileSpeed: 1,
    projectileCount: 0,
    duration: 1,
    pickupRange: 3.2,
    xpGain: 1,
    goldGain: 1,
    armor: 0,
    dodge: 0,
    luck: 0,
    lifesteal: 0,
    cooldownReduction: 0,
    revives: 0,
    knockback: 1,
    thorns: 0,
    statusDamage: 0,
    maxShield: 0,
  }
}

export type EntityKind = 'player' | 'enemy' | 'projectile' | 'pickup' | 'prop'

export type PickupKind = 'xp' | 'gold' | 'heal' | 'chest' | 'magnet' | 'bomb' | 'vendor'

export type EnemyAI =
  | 'chaser'
  | 'ranged'
  | 'charger'
  | 'splitter'
  | 'orbiter'
  | 'boss'
  | 'armored' // front-plated: heavily reduces frontal hits, must be flanked / burned / blown up
  | 'buffer' // pulses an empower aura that speeds & strengthens nearby aliens
  | 'flanker' // swings wide to the player's sides instead of charging head-on
  | 'healer' // hangs back and periodically mends nearby wounded allies — kill it first or the swarm never thins

export interface Enemy {
  id: number
  defId: string
  alive: boolean
  x: number
  z: number
  vx: number
  vz: number
  facing: number
  radius: number
  hp: number
  maxHp: number
  damage: number
  speed: number
  ai: EnemyAI
  xp: number
  gold: number
  isElite: boolean
  isBoss: boolean
  isMiniBoss: boolean // named champion beat (small HP bar + chest reward), tougher than an elite
  // runtime ai / status
  attackCd: number
  stagger: number // knockback velocity decay timer
  flash: number // hit-flash timer (for render)
  contactCd: number // per-source contact-attack cooldown (MP1: each enemy hits ~every CONTACT_CD, so density bites)
  slow: number // movement slow factor 0..1 remaining time
  slowAmt: number
  burn: number // remaining burn time
  burnDps: number
  poison: number
  poisonDps: number
  freeze: number // frozen time
  bleedStacks: number
  aiTimer: number
  summonCd: number
  spawnedAt: number
  // behavior-diverse AI runtime
  frontArmor: number // 0..1 fraction of frontal damage blocked (armored type)
  empoweredT: number // remaining seconds of a buffer's empower aura (faster + harder-hitting)
  aiSide: number // flanker strafe side (+1/-1), chosen once at spawn
  // DoT attribution (for the end-of-run damage-by-weapon recap)
  burnSrc?: string // ownerWeapon that applied the current burn
  poisonSrc?: string // ownerWeapon that applied the current poison
  color: number
  scale: number
  splitInto?: string
  splitCount?: number
  // boss movesets / phases
  patternIdx: number // which attack in the boss's pattern list comes next
  enraged: boolean // HP-phase flag: faster, shorter cooldowns, extra telegraphs
  bossShield: number // seconds of damage-immunity window (Shield boss power)
  affix: string // elite/mini-boss behavioral modifier ('' | 'warded' | 'shielded' | …) — reshapes the fight, not just bigger HP
  shieldCd: number // 'shielded' affix: countdown to the next damage-immunity pulse
}

export type ProjectileBehavior =
  | 'straight'
  | 'homing'
  | 'orbit'
  | 'boomerang'
  | 'lobbed'
  | 'beam'
  | 'aura'
  | 'spinner'

export interface Projectile {
  id: number
  ownerWeapon: string
  alive: boolean
  x: number
  z: number
  y: number
  vx: number
  vz: number
  vy: number
  radius: number
  damage: number
  crit: boolean
  behavior: ProjectileBehavior
  life: number // remaining lifetime (s)
  pierce: number // remaining enemies it can pass through
  bounce: number
  speed: number
  color: number
  knockback: number
  // per-behavior state
  angle: number
  orbitRadius: number
  orbitSpeed: number
  homingTurn: number
  returning: boolean
  hitCooldown: number // re-hit interval for auras/orbitals/boomerangs (0 = single hit set)
  hitClear: number // countdown until hitSet is cleared (enables periodic re-hits)
  hitSet: Set<number> // enemy ids already hit (for pierce-once)
  returnedToPlayer: boolean // boomerang has started its return arc
  // status application
  burn: number
  poison: number
  slow: number
  freeze: number
  chain: number // remaining chain-lightning jumps
  chainBase: number // spawn chain count, restored each multi-hit cycle so re-hitting weapons re-chain
  explodeRadius: number
  explodeDamage: number
  pull: number // >0 = gravitational pull strength on enemies in an outer shell
  trail?: boolean
  onExpireSpawn?: string
  srcEnemy?: string // for enemy shots: the defId of the shooter (cause-of-death attribution)
}

export interface Pickup {
  id: number
  kind: PickupKind
  alive: boolean
  x: number
  z: number
  y: number
  vy: number
  value: number
  radius: number
  magnetized: boolean
  bob: number
  rarity?: Rarity
  /** XP-gem tier 1..3 (small/medium/large) — drives sprite, size and on-map merging. */
  tier?: number
}

export interface FloatingText {
  id: number
  x: number
  z: number
  y: number
  text: string
  color: number
  life: number
  maxLife: number
  vy: number
  crit: boolean
}

export interface Particle {
  id: number
  x: number
  z: number
  y: number
  vx: number
  vy: number
  vz: number
  life: number
  maxLife: number
  color: number
  size: number
}

/** Per-weapon runtime state (level, cooldown timer, etc.). */
export interface WeaponState {
  defId: string
  level: number
  cooldown: number // seconds until next fire
  evolved: boolean
  // scratch state for stateful weapons (orbitals etc.)
  scratch: Record<string, number>
  // MP3: ids of this weapon's PERSISTENT orbital/spinner projectiles (reused across cooldowns,
  // not re-spawned), so orbitals rotate continuously instead of blinking each refresh.
  orbitalIds?: number[]
}

/** Per-passive-item runtime state. */
export interface ItemState {
  defId: string
  level: number
}
